"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ProductPreviewMainSlider = void 0;
const tslib_1 = require("tslib");
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const common_types_1 = require("@as-react/common-types");
const cotopaxi_1 = require("@as/cotopaxi");
const Video_1 = require("../../../video/Video");
const VideoUtil_1 = require("../../../../util/VideoUtil");
const LabelsOverlay_1 = tslib_1.__importDefault(require("../../../common/labels-overlay/LabelsOverlay"));
const useSlidesDisplayState_1 = require("../../../../hooks/useSlidesDisplayState");
const useProductMedia_1 = require("../../product-media/useProductMedia");
const ProductPreview_1 = require("../ProductPreview");
const HtmlInclude_1 = require("../../../common/html-include/HtmlInclude");
const andes_react_1 = require("@yonderland/andes-react");
const BACKGROUND_LOAD_IMAGES_THRESHOLD = 2;
const ProductPreviewSlide = (_a) => {
    var { shouldLoadBackgroundImage, smallImagePath } = _a, rest = tslib_1.__rest(_a, ["shouldLoadBackgroundImage", "smallImagePath"]);
    const [didLoadImage, setDidLoadImage] = (0, react_1.useState)(!shouldLoadBackgroundImage);
    (0, react_1.useEffect)(() => {
        if (!shouldLoadBackgroundImage) {
            return;
        }
        setDidLoadImage(false);
        const imageLoader = new Image();
        imageLoader.src = smallImagePath;
        imageLoader.decoding = 'sync';
        imageLoader.onload = () => {
            setDidLoadImage(true);
        };
    }, [smallImagePath, shouldLoadBackgroundImage]);
    return (0, jsx_runtime_1.jsx)("div", { children: didLoadImage ? (0, jsx_runtime_1.jsx)(cotopaxi_1.ProductPreviewMainSlide, Object.assign({ smallImagePath: smallImagePath }, rest)) : null });
};
const getStyleFragment = () => `
  .swiper-slide.slidesPerView-1-1 {
    width: calc(90% + 4px);
  }
  .swiper-slide.slidesPerView-1-2 {
    width: calc(82.5% + 4px);
  }
  .swiper-slide.slidesPerView-1-1,
  .swiper-slide.slidesPerView-1-2 {
    margin-right: 4px;
  }
`;
const ProductPreviewMainSlider = ({ activeSlideIndex, selectedColorId, onActiveSlideIndexChange, onUpdateSlideInView, showLabels, popoverHandlers, slidesPerView, }) => {
    var _a;
    const { mediaCollection, alt, labelsConfig } = (0, useProductMedia_1.useProductMedia)();
    const gallerySwiper = (0, react_1.useRef)(null);
    const { handlePopoverOpen } = popoverHandlers;
    const slidesDisplayState = (0, useSlidesDisplayState_1.useSlidesDisplayState)(activeSlideIndex, mediaCollection.length, (_a = mediaCollection === null || mediaCollection === void 0 ? void 0 : mediaCollection[activeSlideIndex + 1]) === null || _a === void 0 ? void 0 : _a.isVideo);
    const firstSlideRef = (0, react_1.useRef)(null);
    const isObserverTriggered = (0, react_1.useRef)(false);
    const isClient = (0, andes_react_1.useIsClient)();
    const onGallerySlideChange = function () {
        const { snapIndex } = this;
        if (snapIndex !== activeSlideIndex) {
            onActiveSlideIndexChange(snapIndex);
            onUpdateSlideInView === null || onUpdateSlideInView === void 0 ? void 0 : onUpdateSlideInView(snapIndex, ProductPreview_1.SlideInViewFrom.CAROUSEL);
        }
    };
    (0, react_1.useEffect)(() => {
        if (!onUpdateSlideInView || !firstSlideRef.current || isObserverTriggered.current) {
            return;
        }
        const observer = new IntersectionObserver(([entry]) => {
            if (entry === null || entry === void 0 ? void 0 : entry.isIntersecting) {
                onUpdateSlideInView(0, ProductPreview_1.SlideInViewFrom.CAROUSEL);
                observer.disconnect();
                isObserverTriggered.current = true;
            }
        }, {
            threshold: 0.1,
        });
        observer.observe(firstSlideRef.current);
        return () => {
            observer.disconnect();
        };
    }, [selectedColorId, onUpdateSlideInView, firstSlideRef.current]);
    (0, react_1.useEffect)(() => {
        var _a;
        (_a = gallerySwiper.current) === null || _a === void 0 ? void 0 : _a.on('slideChange', onGallerySlideChange);
        return () => {
            var _a;
            (_a = gallerySwiper.current) === null || _a === void 0 ? void 0 : _a.off('slideChange');
        };
    }, [mediaCollection]);
    const renderSlide = (0, react_1.useCallback)((image, index, altCopy) => ((0, jsx_runtime_1.jsx)("div", { ref: index === 0 ? firstSlideRef : undefined, children: slidesDisplayState[index] && ((0, jsx_runtime_1.jsx)(ProductPreviewSlide, { shouldLoadBackgroundImage: index > BACKGROUND_LOAD_IMAGES_THRESHOLD, smallImagePath: image.sourceSet[common_types_1.ProductImageSizesPortraitEnum.detail], largeImagePath: image.sourceSet[common_types_1.ProductImageSizesPortraitEnum.zoom], videoComponent: image.video ? ((0, jsx_runtime_1.jsx)(Video_1.Video, { url: image.video.productVideoDetail.url, playerType: (0, VideoUtil_1.getVideoPlayerType)(image.video.productVideoDetail.url), stop: index !== activeSlideIndex })) : undefined, alt: altCopy, onClick: !image.isVideo ? handlePopoverOpen : undefined })) }, `image_${index}`)), [activeSlideIndex, slidesDisplayState]);
    const productMainSliderCarouselConfig = {
        variant: cotopaxi_1.CarouselVariant.MAIN,
        arrowsConfig: {
            variant: cotopaxi_1.SliderNavVariant.SEMICIRCLE,
        },
        showPagination: true,
        configurationOverrides: {
            slideClass: isClient
                ? 'swiper-slide'
                : `swiper-slide slidesPerView-${slidesPerView.toString().replace('.', '-')}`,
            normalizeSlideIndex: false,
            shouldSwiperUpdate: true,
            getSwiper: (instance) => {
                gallerySwiper.current = instance;
            },
            spaceBetween: 4,
            centeredSlides: true,
            watchOverflow: true,
            slidesPerView,
            centeredSlidesBounds: slidesPerView > 1,
        },
    };
    const renderMainCarouselOverlay = showLabels ? ((0, jsx_runtime_1.jsx)(LabelsOverlay_1.default, Object.assign({}, labelsConfig, { hasWideGutter: true, dataQA: "product_image_overlay" }))) : undefined;
    (0, react_1.useEffect)(() => {
        var _a;
        (_a = gallerySwiper.current) === null || _a === void 0 ? void 0 : _a.slideTo(activeSlideIndex);
    }, [activeSlideIndex]);
    (0, react_1.useEffect)(() => {
        var _a;
        (_a = gallerySwiper.current) === null || _a === void 0 ? void 0 : _a.update();
    }, [mediaCollection]);
    return isClient ? ((0, jsx_runtime_1.jsx)(cotopaxi_1.Carousel, Object.assign({}, productMainSliderCarouselConfig, { dataQA: "product_main_slider", overlay: renderMainCarouselOverlay, children: mediaCollection.map((image, index) => renderSlide(image, index, alt)) }))) : ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(HtmlInclude_1.HtmlInclude, { styleFragment: getStyleFragment() }), (0, jsx_runtime_1.jsx)(cotopaxi_1.Carousel, Object.assign({}, productMainSliderCarouselConfig, { dataQA: "product_main_slider", overlay: renderMainCarouselOverlay, children: mediaCollection.map((image, index) => renderSlide(image, index, alt)) }))] }));
};
exports.ProductPreviewMainSlider = ProductPreviewMainSlider;
